/**
 * MechanicsAnalyzer - Análisis experto de mecánica de tiro
 * 501 Dart League
 * 
 * El jugador graba un vídeo corto de su tirada y recibe un análisis
 * detallado de su técnica con puntuaciones y consejos personalizados.
 * 
 * Disponible 1 vez cada 30 días por usuario.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';
import './styles/mechanics.css';

// Nombres legibles de cada categoría
const CATEGORY_LABELS = {
  postura: 'Postura',
  agarre: 'Agarre',
  codo: 'Codo',
  brazo: 'Brazo',
  release: 'Release',
  followThrough: 'Follow-through',
  consistencia: 'Consistencia',
};

const CATEGORY_ICONS = {
  postura: '🧍',
  agarre: '✋',
  codo: '💪',
  brazo: '🦾',
  release: '🎯',
  followThrough: '➡️',
  consistencia: '🔄',
};

const LEVEL_LABELS = {
  principiante: { text: 'Principiante', color: '#e74c3c' },
  intermedio: { text: 'Intermedio', color: '#f39c12' },
  avanzado: { text: 'Avanzado', color: '#2ecc71' },
  profesional: { text: 'Profesional', color: '#00C6CF' },
};

const RECORDING_DURATION = 8; // seconds

const MechanicsAnalyzer = ({ onBack }) => {
  // ─── Estado principal ───
  const [phase, setPhase] = useState('loading'); // loading, instructions, camera, recording, countdown, review, uploading, results, history, cooldown
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyAnalysis, setHistoryAnalysis] = useState(null);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // ─── Cámara y grabación ───
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const [countdown, setCountdown] = useState(3);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const recordingTimerRef = useRef(null);
  const [reviewVideoUrl, setReviewVideoUrl] = useState(null);
  const reviewVideoRef = useRef(null);

  // Asegurar que el vídeo muestra el stream cuando el ref se conecta al DOM
  const setVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
    }
  }, []);

  // ─── Cargar estado inicial ───
  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpiar cámara al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (reviewVideoUrl) URL.revokeObjectURL(reviewVideoUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatus = async () => {
    try {
      setPhase('loading');
      const s = await api.getMechanicsStatus();
      setStatus(s);

      if (s.canAnalyze) {
        setPhase('instructions');
      } else {
        // Tiene análisis previo y está en cooldown
        setPhase('cooldown');
      }
    } catch (err) {
      console.error('Error cargando estado:', err);
      setError('No se pudo cargar el estado. Inténtalo de nuevo.');
      setPhase('instructions');
    }
  };

  const loadHistory = async () => {
    try {
      const h = await api.getMechanicsHistory();
      setHistory(h);
      setPhase('history');
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
  };

  // ─── Cámara ───
  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setPhase('camera');
    } catch (err) {
      console.error('Error accediendo a la cámara:', err);
      if (err.name === 'NotAllowedError') {
        setError('Necesitas permitir el acceso a la cámara para grabar tu tirada.');
      } else if (err.name === 'NotFoundError') {
        setError('No se detectó ninguna cámara en tu dispositivo.');
      } else {
        setError('Error al acceder a la cámara. Comprueba los permisos del navegador.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // ─── Countdown antes de grabar ───
  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setCountdown(3);

    let count = 3;
    const timer = setInterval(() => {
      count--;
      setCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        startRecording();
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Grabar ───
  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setRecordingProgress(0);
    setPhase('recording');

    // Elegir codec soportado
    let mimeType = 'video/webm;codecs=vp8';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/mp4';
      }
    }

    const recorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 1500000, // 1.5 Mbps — calidad decente, tamaño razonable
    });

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      // Ir a fase review en vez de enviar directamente
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setReviewVideoUrl(url);
      setPhase('review');
    };

    mediaRecorderRef.current = recorder;
    recorder.start(500); // chunk cada 500ms

    // Progress bar
    let elapsed = 0;
    recordingTimerRef.current = setInterval(() => {
      elapsed += 100;
      const progress = Math.min(100, (elapsed / (RECORDING_DURATION * 1000)) * 100);
      setRecordingProgress(progress);

      if (elapsed >= RECORDING_DURATION * 1000) {
        clearInterval(recordingTimerRef.current);
        recorder.stop();
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Repetir grabación ───
  const retryRecording = useCallback(() => {
    if (reviewVideoUrl) URL.revokeObjectURL(reviewVideoUrl);
    setReviewVideoUrl(null);
    chunksRef.current = [];
    // Re-activar cámara
    startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewVideoUrl]);

  // ─── Enviar vídeo grabado ───
  const sendRecording = useCallback(async () => {
    if (reviewVideoUrl) URL.revokeObjectURL(reviewVideoUrl);
    setReviewVideoUrl(null);
    await processRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewVideoUrl]);

  // ─── Procesar y enviar ───
  const processRecording = useCallback(async () => {
    stopCamera();
    setPhase('uploading');
    setError(null);

    try {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });

      // Convertir a base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Quitar prefijo data:video/webm;base64,
          const result = reader.result.split(',')[1];
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Enviar al backend
      const result = await api.analyzeMechanics(base64, 'video/webm');
      setAnalysis(result);
      setPhase('results');
    } catch (err) {
      console.error('Error procesando análisis:', err);
      setError(err.message || 'Error al procesar el análisis. Inténtalo de nuevo.');
      setPhase('instructions');
    }
  }, []);

  // ─── Feedback ───
  const sendFeedback = async (type) => {
    if (!analysis?._id || feedbackSent) return;
    try {
      await api.sendMechanicsFeedback(analysis._id, type);
      setFeedbackSent(true);
    } catch (err) {
      console.error('Error enviando feedback:', err);
    }
  };

  // ─── Score bar color ───
  const getScoreColor = (score) => {
    if (score >= 8) return '#2ecc71';
    if (score >= 6) return '#f39c12';
    if (score >= 4) return '#e67e22';
    return '#e74c3c';
  };

  // ─── RENDER ───

  // Loading
  if (phase === 'loading') {
    return (
      <div className="mechanics-container">
        <div className="mechanics-loading">
          <div className="mechanics-spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  // Results view (also for history detail)
  const renderResults = (data, isHistory = false) => {
    if (!data) return null;
    const levelInfo = LEVEL_LABELS[data.level] || LEVEL_LABELS.intermedio;

    return (
      <div className="mechanics-results">
        {/* Overall score */}
        <div className="mechanics-overall">
          <div className="mechanics-overall-score" style={{ borderColor: levelInfo.color }}>
            <span className="mechanics-overall-number">{data.overall}</span>
            <span className="mechanics-overall-max">/10</span>
          </div>
          <div className="mechanics-overall-info">
            <span className="mechanics-level-badge" style={{ background: levelInfo.color }}>
              {levelInfo.text}
            </span>
            <span className="mechanics-overall-date">
              {new Date(data.createdAt).toLocaleDateString('es-ES', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Score bars */}
        <div className="mechanics-scores">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const score = data.scores?.[key] || 0;
            const detail = data.details?.[key] || '';
            return (
              <div key={key} className="mechanics-score-row">
                <div className="mechanics-score-header">
                  <span className="mechanics-score-icon">{CATEGORY_ICONS[key]}</span>
                  <span className="mechanics-score-label">{label}</span>
                  <span className="mechanics-score-value" style={{ color: getScoreColor(score) }}>
                    {score}
                  </span>
                </div>
                <div className="mechanics-score-bar-bg">
                  <div
                    className="mechanics-score-bar-fill"
                    style={{
                      width: `${score * 10}%`,
                      background: getScoreColor(score),
                    }}
                  />
                </div>
                {detail && <p className="mechanics-score-detail">{detail}</p>}
              </div>
            );
          })}
        </div>

        {/* Tips */}
        {data.tips && data.tips.length > 0 && (
          <div className="mechanics-tips">
            <h3 className="mechanics-tips-title">Consejos para mejorar</h3>
            {data.tips.map((tip, i) => (
              <div key={i} className="mechanics-tip-item">
                <span className="mechanics-tip-number">{i + 1}</span>
                <p className="mechanics-tip-text">{tip}</p>
              </div>
            ))}
          </div>
        )}

        {/* Feedback (solo si no es historial y no se ha enviado) */}
        {!isHistory && !feedbackSent && (
          <div className="mechanics-feedback">
            <p className="mechanics-feedback-question">¿Te ha parecido útil el análisis?</p>
            <div className="mechanics-feedback-buttons">
              <button className="mechanics-feedback-btn positive" onClick={() => sendFeedback('positive')}>
                👍 Sí, bastante
              </button>
              <button className="mechanics-feedback-btn negative" onClick={() => sendFeedback('negative')}>
                👎 No mucho
              </button>
            </div>
          </div>
        )}

        {feedbackSent && !isHistory && (
          <div className="mechanics-feedback-thanks">
            ¡Gracias por tu valoración!
          </div>
        )}

        {/* Disclaimer */}
        <div className="mechanics-disclaimer">
          Este análisis es orientativo y no sustituye la valoración de un entrenador profesional en persona.
        </div>
      </div>
    );
  };

  return (
    <div className="mechanics-container">
      {/* Header */}
      <header className="mechanics-header">
        <div className="mechanics-header-top">
          <button className="mechanics-back-btn" onClick={() => {
            stopCamera();
            if (phase === 'history' && !historyAnalysis) {
              setPhase(status?.canAnalyze ? 'instructions' : 'cooldown');
            } else if (historyAnalysis) {
              setHistoryAnalysis(null);
            } else {
              onBack();
            }
          }}>
            ← Volver
          </button>
          {phase !== 'history' && status && !status.canAnalyze && (
            <button className="mechanics-history-btn" onClick={loadHistory}>
              📋 Historial
            </button>
          )}
          {phase === 'instructions' && history.length === 0 && (
            <button className="mechanics-history-btn" onClick={loadHistory}>
              📋 Historial
            </button>
          )}
        </div>
        <div className="mechanics-header-icon">🎯</div>
        <h1 className="mechanics-title">Analiza tu Mecánica</h1>
        <p className="mechanics-subtitle">
          {phase === 'history' 
            ? 'Tus análisis anteriores'
            : 'Graba tu tirada y recibe un análisis técnico detallado'
          }
        </p>
      </header>

      {/* Error */}
      {error && (
        <div className="mechanics-error">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      {/* ─── COOLDOWN: no puede analizar ─── */}
      {phase === 'cooldown' && status && (
        <div className="mechanics-cooldown">
          <div className="mechanics-cooldown-icon">⏳</div>
          <h2>Próximo análisis en {status.daysRemaining} día{status.daysRemaining !== 1 ? 's' : ''}</h2>
          <p>Puedes hacer un análisis cada 30 días para ver tu evolución.</p>

          {status.lastAnalysis && (
            <div className="mechanics-last-result">
              <h3>Tu último análisis</h3>
              {renderResults({
                ...status.lastAnalysis, 
                createdAt: status.lastAnalysis.date,
                details: {},
                tips: [],
              }, true)}
            </div>
          )}

          <button className="mechanics-history-btn full" onClick={loadHistory}>
            📋 Ver historial completo
          </button>
        </div>
      )}

      {/* ─── INSTRUCCIONES ─── */}
      {phase === 'instructions' && (
        <div className="mechanics-instructions">
          <div className="mechanics-instruction-card">
            <div className="mechanics-instruction-step">1</div>
            <div className="mechanics-instruction-content">
              <h3>Coloca el dispositivo</h3>
              <p>Pon el móvil o portátil a tu lado, que se vea tu <strong>cuerpo de perfil</strong> y todo el brazo de tiro.</p>
            </div>
          </div>

          <div className="mechanics-instruction-card">
            <div className="mechanics-instruction-step">2</div>
            <div className="mechanics-instruction-content">
              <h3>Tira 3 dardos normal</h3>
              <p>Se grabará un vídeo de <strong>{RECORDING_DURATION} segundos</strong>. Tira como lo harías en una partida real, sin forzar.</p>
            </div>
          </div>

          <div className="mechanics-instruction-card">
            <div className="mechanics-instruction-step">3</div>
            <div className="mechanics-instruction-content">
              <h3>Recibe tu análisis</h3>
              <p>Recibirás puntuaciones en 7 aspectos técnicos y consejos personalizados para mejorar.</p>
            </div>
          </div>

          <div className="mechanics-instruction-tips">
            <h4>💡 Para un mejor análisis:</h4>
            <ul>
              <li>Buena iluminación — que se vea bien tu brazo</li>
              <li>Vista lateral — de perfil es ideal</li>
              <li>Distancia media — que se vea de cintura para arriba</li>
              <li>Tira natural — no cambies tu mecánica para la cámara</li>
            </ul>
          </div>

          <button className="mechanics-start-btn" onClick={startCamera}>
            📹 Empezar grabación
          </button>

          {status && !status.canAnalyze && (
            <p className="mechanics-cooldown-note">
              Próximo análisis disponible en {status.daysRemaining} días
            </p>
          )}
        </div>
      )}

      {/* ─── CÁMARA LISTA ─── */}
      {(phase === 'camera' || phase === 'countdown' || phase === 'recording') && (
        <div className="mechanics-camera">
          <div className="mechanics-video-wrapper">
            <video
              ref={setVideoRef}
              autoPlay
              playsInline
              muted
              className="mechanics-video"
            />

            {/* Countdown overlay */}
            {phase === 'countdown' && (
              <div className="mechanics-countdown-overlay">
                <div className="mechanics-countdown-number">{countdown}</div>
                <p>Prepárate para tirar...</p>
              </div>
            )}

            {/* Recording indicator */}
            {phase === 'recording' && (
              <div className="mechanics-recording-indicator">
                <span className="mechanics-rec-dot"></span>
                GRABANDO
              </div>
            )}
          </div>

          {/* Progress bar during recording */}
          {phase === 'recording' && (
            <div className="mechanics-progress-bar-bg">
              <div
                className="mechanics-progress-bar-fill"
                style={{ width: `${recordingProgress}%` }}
              />
            </div>
          )}

          {/* Start recording button */}
          {phase === 'camera' && (
            <div className="mechanics-camera-actions">
              <p className="mechanics-camera-hint">
                Colócate de perfil. Cuando estés listo, pulsa grabar.
              </p>
              <button className="mechanics-record-btn" onClick={startCountdown}>
                <span className="mechanics-record-dot"></span>
                Grabar {RECORDING_DURATION}s
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── REVISAR VÍDEO ─── */}
      {phase === 'review' && reviewVideoUrl && (
        <div className="mechanics-review">
          <h2 className="mechanics-review-title">Revisa tu grabación</h2>
          <div className="mechanics-video-wrapper">
            <video
              ref={reviewVideoRef}
              src={reviewVideoUrl}
              autoPlay
              loop
              playsInline
              muted
              className="mechanics-video"
            />
          </div>
          <p className="mechanics-review-hint">
            ¿Se ve bien tu tirada? Si no estás conforme, puedes repetirla.
          </p>
          <div className="mechanics-review-actions">
            <button className="mechanics-retry-btn" onClick={retryRecording}>
              🔄 Repetir
            </button>
            <button className="mechanics-send-btn" onClick={sendRecording}>
              ✅ Enviar análisis
            </button>
          </div>
        </div>
      )}

      {/* ─── PROCESANDO ─── */}
      {phase === 'uploading' && (
        <div className="mechanics-uploading">
          <div className="mechanics-uploading-animation">
            <div className="mechanics-spinner large"></div>
            <div className="mechanics-uploading-icon">🎯</div>
          </div>
          <h2>Analizando tu mecánica...</h2>
          <p>Esto puede tardar unos segundos</p>
          <div className="mechanics-uploading-steps">
            <div className="mechanics-uploading-step active">
              <span>📹</span> Procesando vídeo
            </div>
            <div className="mechanics-uploading-step">
              <span>🔍</span> Analizando postura y movimiento
            </div>
            <div className="mechanics-uploading-step">
              <span>📊</span> Generando informe
            </div>
          </div>
        </div>
      )}

      {/* ─── RESULTADOS ─── */}
      {phase === 'results' && analysis && (
        <>
          <div className="mechanics-results-header">
            <h2>Tu Análisis Técnico</h2>
          </div>
          {renderResults(analysis)}
          <div className="mechanics-results-actions">
            <button className="mechanics-back-to-menu-btn" onClick={onBack}>
              ← Volver al menú
            </button>
          </div>
        </>
      )}

      {/* ─── HISTORIAL ─── */}
      {phase === 'history' && !historyAnalysis && (
        <div className="mechanics-history">
          {history.length === 0 ? (
            <div className="mechanics-history-empty">
              <span>📋</span>
              <p>Aún no tienes análisis. ¡Haz tu primer análisis!</p>
            </div>
          ) : (
            <div className="mechanics-history-list">
              {history.map((item) => {
                const levelInfo = LEVEL_LABELS[item.level] || LEVEL_LABELS.intermedio;
                return (
                  <button
                    key={item._id}
                    className="mechanics-history-item"
                    onClick={() => setHistoryAnalysis(item)}
                  >
                    <div className="mechanics-history-score" style={{ borderColor: levelInfo.color }}>
                      {item.overall}
                    </div>
                    <div className="mechanics-history-info">
                      <span className="mechanics-history-level" style={{ color: levelInfo.color }}>
                        {levelInfo.text}
                      </span>
                      <span className="mechanics-history-date">
                        {new Date(item.createdAt).toLocaleDateString('es-ES', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="mechanics-history-arrow">→</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── DETALLE DE HISTORIAL ─── */}
      {phase === 'history' && historyAnalysis && (
        <>
          <div className="mechanics-results-header">
            <h2>Análisis del {new Date(historyAnalysis.createdAt).toLocaleDateString('es-ES', {
              day: 'numeric', month: 'long'
            })}</h2>
          </div>
          {renderResults(historyAnalysis, true)}
        </>
      )}
    </div>
  );
};

export default MechanicsAnalyzer;
